import axios, { HttpStatusCode } from "axios";
import { servicesUrlConfig } from "shared/config/setting";

const ORGS_SERVICE_URL = servicesUrlConfig.ORGS_SERVICE_URL;

export const getCurrentUserOrgsService = async (token) => {
  const url = servicesUrlConfig.ORGS_API.me;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.get(url, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to fetch user's organizations");
    }
  } catch (error) {
    console.error("Error during fetching user's organizations", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const getOrganizationsListService = async (token) => {
  const url = servicesUrlConfig.ORGS_API.get_all; // Use the updated URL for fetching all organizations
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.get(url, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to fetch organizations list");
    }
  } catch (error) {
    console.error("Error during fetching organizations", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const createOrganization = async (token, name, description) => {
  const url = servicesUrlConfig.ORGS_API.create;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const data = {
    name,
    description,
  };
  try {
    const response = await axios.post(url, data, { headers });
    if (response.status === HttpStatusCode.Created) {
      return response.data;
    } else {
      throw new Error("Failed to create organization");
    }
  } catch (error) {
    console.error("Error during creating organization", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const updateOrganization = async (token, orgId, name, description) => {
  const url = `${ORGS_SERVICE_URL}/${orgId}`;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const data = {
    name,
    description,
  };

  try {
    const response = await axios.patch(url, data, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to update organization");
    }
  } catch (error) {
    console.error("Error during updating organization", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const deleteOrganization = async (token, orgId) => {
  const url = `${ORGS_SERVICE_URL}/${orgId}`;
  const headers = {
    accept: "*/*",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.delete(url, { headers });
    if (response.status === 204) {
      return { message: "Organization deleted successfully" };
    } else {
      throw new Error("Failed to delete organization");
    }
  } catch (error) {
    console.error("Error during deleting organization", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const getOrganizationMembers = async (token, orgId, page = 1, perPage = 10) => {
  const params = new URLSearchParams({ page, per_page: perPage });
  const url = `${ORGS_SERVICE_URL}/${orgId}/members?${params.toString()}`;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.get(url, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to fetch organizations list");
    }
  } catch (error) {
    console.error("Error during fetching organizations", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const addOrganizationMembers = async (token, orgId, username, roles) => {
  const url = `${ORGS_SERVICE_URL}/${orgId}/members`;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const data = {
    username,
    roles,
  };

  try {
    const response = await axios.put(url, data, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to add organization member");
    }
  } catch (error) {
    console.error("Error during adding organization member", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const updateOrganizationMemberRoles = async (
  token,
  orgId,
  username,
  roles
) => {
  const url = `${ORGS_SERVICE_URL}/${orgId}/members/${username}`;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  try {
    const response = await axios.patch(url, roles, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to add organization member");
    }
  } catch (error) {
    console.error("Error during adding organization member", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const deleteOrganizationMember = async (token, orgId, username) => {
  const url = `${ORGS_SERVICE_URL}/${orgId}/members/${username}`;
  const headers = {
    accept: "*/*",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.delete(url, { headers });
    if (response.status === 204) {
      return { message: "Organization member removed successfully" };
    } else {
      throw new Error("Failed to remove organization member");
    }
  } catch (error) {
    console.error("Error during removing organization member", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const getOrganizationRobots = async (token, orgId, page = 1, perPage = 10) => {
  const params = new URLSearchParams({ page, per_page: perPage });
  const url = `${ORGS_SERVICE_URL}/${orgId}/robots?${params.toString()}`;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.get(url, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to fetch org robots");
    }
  } catch (error) {
    console.error("Error during fetching org robot", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const addOrganizationRobot = async (token, orgId, robotId) => {
  const url = `${ORGS_SERVICE_URL}/${orgId}/robots?robot_id=${robotId}`;
  const headers = {
    accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.post(url, null, { headers });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to add organization robot");
    }
  } catch (error) {
    console.error("Error during adding organization robot", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const deleteOrganizationRobot = async (token, orgId, robotId) => {
  const url = `${ORGS_SERVICE_URL}/${orgId}/robots?robot_id=${robotId}`;
  const headers = {
    accept: "*/*",
    Authorization: `Bearer ${token}`,
  };
  try {
    const response = await axios.delete(url, { headers });
    if (response.status === 204) {
      return { message: "Organization robot removed successfully" };
    } else {
      throw new Error("Failed to remove organization robot");
    }
  } catch (error) {
    console.error("Error during removing organization robot", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};
